// confirm_dialog.jsx — Reusable <ConfirmDialog /> primitive.
// Bottom-sheet overlay consolidating the inline confirm markup that used to
// live in programs.jsx (PConfirmSheet), library.jsx (LibConfirmSheet), and
// screens_wired.jsx (inline reset-all sheet). Visual language mirrors LLSheet:
// dimmed backdrop + slide-up surface with rounded top corners. It renders in a
// viewport-fixed portal (like LLSheet's SheetLayer) so a dialog opened from a
// scrolled screen sits at the bottom of the screen, not of the scroll content,
// and Android / browser Back closes it.
//
// Props:
//   open         — bool   — controls visibility
//   title        — string — headline shown in the sheet
//   body         — string — optional supporting copy
//   confirmLabel — string — default 'Delete'
//   cancelLabel  — string — default 'Cancel'
//   tone         — 'default' | 'danger' — danger = red confirm button (LL.danger)
//   onConfirm    — fn     — called when user confirms (parent handles close)
//   onCancel     — fn     — called when user cancels / taps backdrop

import React from 'react';
import { createPortal } from 'react-dom';
import { LL } from './tokens.jsx';
import { useSheetHistory } from './sheet_history.js';

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}) {
  const danger = tone === 'danger';
  const confirmBg = danger ? 'oklch(0.58 0.19 25)' : LL.accent;
  const confirmFg = danger ? 'oklch(0.99 0 0)' : LL.accentInk;
  useSheetHistory(!!open, onCancel);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel && onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const dialog = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, pointerEvents: 'none' }}>
      {/* Backdrop — matches LLSheet */}
      <div
        onClick={onCancel}
        style={{
          position: 'absolute', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
          transition: `opacity ${LL.motion.base}ms ${LL.ease}`,
        }}
      />
      {/* Sheet — matches LLSheet slide-up animation */}
      <div
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 201,
          margin: '0 auto', maxWidth: 560,
          background: LL.surface, borderTopLeftRadius: LL.r.lg, borderTopRightRadius: LL.r.lg,
          border: `1px solid ${LL.line}`, borderBottom: 'none',
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateY(0)' : 'translateY(105%)',
          // Fully hide when closed so a scrolled container can never reveal it
          // (mirrors LLSheet); visibility flips after the slide-out finishes.
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          transition: open
            ? `transform ${LL.motion.slow}ms ${LL.ease}, visibility 0s`
            : `transform ${LL.motion.slow}ms ${LL.ease}, visibility 0s linear ${LL.motion.slow}ms`,
          boxShadow: LL.shadow.sheet,
          padding: '12px 20px calc(env(safe-area-inset-bottom, 0px) + 28px)',
          boxSizing: 'border-box',
        }}
      >
        {/* Grabber */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
          <div style={{ width: 40, height: 4, borderRadius: LL.r.xs, background: LL.line2 }} />
        </div>

        <div style={{
          fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg,
          letterSpacing: -0.3, marginBottom: body ? 8 : 18, lineHeight: 1.15,
        }}>{title}</div>
        {body && (
          <div style={{
            fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5,
            marginBottom: 20,
          }}>{body}</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={onConfirm}
            style={{
              height: 50, borderRadius: LL.r.md,
              background: confirmBg, color: confirmFg,
              border: 'none',
              fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600,
              cursor: 'pointer',
            }}
          >{confirmLabel}</button>
          <button
            onClick={onCancel}
            style={{
              height: 50, borderRadius: LL.r.md,
              background: LL.surface2, color: LL.fg,
              border: `1px solid ${LL.line}`,
              fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600,
              cursor: 'pointer',
            }}
          >{cancelLabel}</button>
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}

export { ConfirmDialog };
